import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import OrientationCertificate from './utils//OrientationCertificate';
import PDBuySubscription from "./PDBuySubscription";

const MODULES = [
  { id: "m1",    label: "Module 1",    title: "Deep Thinking",       icon: "🧠" },
  { id: "m2",    label: "Module 2",    title: "IKIGAI",              icon: "∞" },
  { id: "m3",    label: "Module 3",    title: "Attention Signals",   icon: "🎯" },
  { id: "m4",    label: "Module 4",    title: "ROACHES Daily Flow",  icon: "🔄" },
  { id: "m5",    label: "Module 5",    title: "Digital Readiness",   icon: "💻" },
  { id: "m6",    label: "Module 6",    title: "Behavior OS (5W1H)",  icon: "✅" },
  { id: "m7",    label: "Module 7",    title: "GenAI Boundaries",    icon: "🤖" },
  { id: "decl",  label: "Declaration", title: "My Declaration",      icon: "✍️" },
  // { id: "table", label: "All Records", title: "pd_oriented",         icon: "📋" },
];

const ATTENTION_QS = [
  { q: "I get distracted while working",        k: "m3_distracted" },
  { q: "I switch apps frequently",              k: "m3_switch" },
  { q: "I leave tasks unfinished",              k: "m3_unfinished" },
  { q: "I procrastinate important work",        k: "m3_procrastinate" },
  { q: "I struggle to focus for 30 minutes",   k: "m3_focus" },
  { q: "I forget commitments",                  k: "m3_forget" },
  { q: "I consume excessive short-form content",k: "m3_shortform" },
  { q: "I multitask excessively",               k: "m3_multitask" },
];

const ROACHES = [
  { label: "Relationships",          key: "m4_rel" },
  { label: "Occupation / Learning",  key: "m4_occ" },
  { label: "Attention Management",   key: "m4_att" },
  { label: "Confidence",             key: "m4_conf" },
  { label: "Health",                 key: "m4_health" },
  { label: "Entrepreneurship",       key: "m4_ent" },
  { label: "Spirituality / Purpose", key: "m4_spirit" },
];

const READINESS_QS = [
  { q: "I am comfortable using smartphones",  k: "m5_smartphone" },
  { q: "I can learn new apps quickly",        k: "m5_apps" },
  { q: "I can communicate digitally",         k: "m5_communicate" },
  { q: "I can work independently",            k: "m5_independent" },
  { q: "I am willing to experiment",          k: "m5_experiment" },
  { q: "I can dedicate 1 hour daily",         k: "m5_hour" },
  { q: "I am willing to fail and learn",      k: "m5_fail" },
  { q: "I have internet access regularly",    k: "m5_internet" },
];

const GENAI_STATEMENTS = [
  { s: "AI can replace my judgment completely",       k: "m7_replace" },
  { s: "AI can make mistakes",                        k: "m7_mistakes" },
  { s: "I should verify important information",       k: "m7_verify" },
  { s: "AI can help me learn faster",                 k: "m7_learn" },
  { s: "Critical thinking remains my responsibility", k: "m7_thinking" },
  { s: "AI should not decide my values and purpose",  k: "m7_values" },
];

const GENAI_HELPS   = ["Learning", "Research", "Writing", "Creativity", "Planning", "Problem Solving"];
const DECL_COMMITS  = ["Think Deeply", "Discover My IKIGAI", "Improve My Attention", "Follow My Daily ROACHES Flow", "Build Digital Skills", "Use 5W1H Before Acting", "Use GenAI Responsibly"];
const ATTN_KEYS     = ATTENTION_QS.map(x => x.k);
const STATUS_COLOR  = { draft: "bg-gray-700 text-gray-100", submitted: "bg-yellow-600 text-yellow-100", approved: "bg-green-700 text-green-100", rejected: "bg-red-700 text-red-100" };

// ─── Reusable mini components ─────────────────────────────────────────────────
const iClass  = "w-full px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-200 border border-gray-700 focus:outline-none focus:border-indigo-500";
const taClass = "w-full px-2 py-1 bg-gray-800 text-white text-xs rounded placeholder:text-gray-200 border border-gray-700 focus:outline-none focus:border-indigo-500 resize-none";
const selClass= "px-2 py-1 bg-gray-800 text-white text-xs rounded border border-gray-700 focus:outline-none focus:border-indigo-500";

function Label({ text }) {
  return <p className="text-gray-100 text-xs mb-1 mt-3">{text}</p>;
}

function ScoreSelect({ value, onChange, max = 10, disabled }) {
  return (
    <select className={selClass} value={value} onChange={e => onChange(e.target.value)} disabled={disabled}>
      <option value="">—</option>
      {Array.from({ length: max }, (_, i) => i + 1).map(n => <option key={n} value={n}>{n}</option>)}
    </select>
  );
}

function Badge({ status }) {
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${STATUS_COLOR[status] || STATUS_COLOR.draft}`}>
      {status || "draft"}
    </span>
  );
}

function LockBanner({ record }) {
  if (!record?.isLocked) return null;
  return (
    <div className="bg-orange-950 border border-orange-700 rounded-lg p-3 mb-4 space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-orange-400 text-sm">🔒</span>
        <span className="text-orange-300 text-xs font-bold">Record Locked</span>
        <Badge status={record.status} />
      </div>
      <p className="text-orange-200 text-xs">
        Locked by <strong>{record.lockedBy || "Moderator"}</strong>
        {record.lockedAt && ` · ${new Date(record.lockedAt).toLocaleDateString()}`}.
        This record is read-only.
      </p>
      {record.moderatorRemarks && (
        <p className="text-yellow-300 text-xs bg-yellow-900/40 rounded px-2 py-1 mt-1">
          📝 <strong>Remarks:</strong> {record.moderatorRemarks}
        </p>
      )}
    </div>
  );
}

function ScoreRow({ questions, form, setForm, maxScore = 5, disabled }) {
  return (
    <table className="w-full text-xs mt-2">
      <thead>
        <tr className="text-gray-200 border-b border-gray-700">
          <th className="text-left py-2 font-medium">Question</th>
          <th className="text-center py-2 font-medium w-20">Score (1–{maxScore})</th>
        </tr>
      </thead>
      <tbody>
        {questions.map(({ q, k }) => (
          <tr key={k} className="border-b border-gray-800">
            <td className="py-2 text-gray-100">{q}</td>
            <td className="py-2 text-center">
              <ScoreSelect max={maxScore} value={form[k]} onChange={v => setForm({ ...form, [k]: v })} disabled={disabled} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ScoreTotal({ value, max, label }) {
  return (
    <div className="flex items-center justify-between bg-gray-800 rounded px-3 py-2 mt-3">
      <span className="text-gray-100 text-xs">Total Score</span>
      <div className="text-right">
        <span className="text-white font-bold text-base">{value}</span>
        <span className="text-gray-200 text-xs"> / {max}</span>
        {label && <p className="text-indigo-400 text-[10px]">{label}</p>}
      </div>
    </div>
  );
}

// --- Active Subscription Check ---
const getSubscriptionStatus = (subscription) => {
  if (!subscription) return { isActive: false, reason: "no_subscription" };
  const isExpired = subscription.endDate && new Date(subscription.endDate) < new Date();
  if (isExpired) return { isActive: false, reason: "expired" };
  if (!subscription.isActive) return { isActive: false, reason: "inactive" };
  return { isActive: true, reason: "active" };
};

// --- Subscription Inactive Alert Component ---
const SubscriptionInactiveAlert = ({ user, reason, onClose, onSubscribed }) => {
  // const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showPDSubscription, setShowPDSubscription] = useState(false);
  const config = {
    no_subscription: {
      icon: "🚀",
      title: "Unlock Your Entrepreneurial Journey",
      subtitle: "You don't have an active DigitalBoutique subscription yet.",
      color: "cyan",
    },
    expired: {
      icon: "⏳",
      title: "Your Subscription Has Expired",
      subtitle: "Renew now to regain full access to all features.",
      color: "amber",
    },
    inactive: {
      icon: "⚠️",
      title: "Subscription Inactive",
      subtitle: "Your subscription is currently paused or inactive.",
      color: "orange",
    },
  }[reason] ?? {
    icon: "🔒",
    title: "Access Restricted",
    subtitle: "Please check your subscription status.",
    color: "cyan",
  };

  const benefits = [
    { icon: "📊", text: "Full access to orientation records & analytics" },
    { icon: "🧭", text: "Personalized entrepreneur roadmap & milestones" },
    { icon: "🔓", text: "Unlock all modules — Navigator, Insights & more" },
    { icon: "🤝", text: "Community access & mentor connect" },
    { icon: "📁", text: "Save, update & track your progress anytime" },
  ];

  const borderColor = {
    cyan: "border-cyan-500/40",
    amber: "border-amber-500/40",
    orange: "border-orange-500/40",
  }[config.color];

  const glowColor = {
    cyan: "shadow-cyan-500/10",
    amber: "shadow-amber-500/10",
    orange: "shadow-orange-500/10",
  }[config.color];

  const accentText = {
    cyan: "text-cyan-400",
    amber: "text-amber-400",
    orange: "text-orange-400",
  }[config.color];

  const accentBg = {
    cyan: "bg-cyan-500/10 hover:bg-cyan-500/20 border-cyan-500/50 text-cyan-300",
    amber: "bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/50 text-amber-300",
    orange: "bg-orange-500/10 hover:bg-orange-500/20 border-orange-500/50 text-orange-300",
  }[config.color];

  const badgeBg = {
    cyan: "bg-cyan-500/15 text-cyan-300 border-cyan-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    orange: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  }[config.color];

  return (
    <div className="min-h-screen bg-[#080d18] flex items-center justify-center p-4">
      <div className={`relative w-full max-w-lg rounded-2xl border ${borderColor} bg-[#0e1320] shadow-2xl ${glowColor} overflow-hidden`}>
        <div className={`h-1 w-full ${config.color === "cyan" ? "bg-gradient-to-r from-transparent via-cyan-500 to-transparent" : config.color === "amber" ? "bg-gradient-to-r from-transparent via-amber-500 to-transparent" : "bg-gradient-to-r from-transparent via-orange-500 to-transparent"}`} />
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-fit px-2 py-1 text-gray-500 hover:text-gray-300 transition-colors text-xs lg:text-sm leading-none"
        >
          ✕
        </button>

        <div className="p-4">
          <div className="text-center mb-2">
            <div className="text-sml lg:text-lg mb-3">{config.icon}</div>
            <h2 className="text-xs lg:text-sm font-bold text-white mb-1">{config.title}</h2>
            <p className="text-[10px] lg:text-xs text-gray-400">{config.subtitle}</p>
          </div>
          <div className="mb-2">
            <p className={`text-xs lg:text-sm font-semibold uppercase tracking-widest ${accentText} mb-3`}>
              What you're missing out on
            </p>
            <ul className="space-y-2">
              {benefits.map((b, i) => (
                <li key={i} className="flex items-center gap-3 text-xs lg:text-sm text-gray-300">
                  <span className="text-xs lg:text-sm">{b.icon}</span>
                  <span className="text-xs lg:text-sm">{b.text}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="border-t border-gray-800 mb-6" />
            <div className={`rounded-xl border px-2 py-1 mb-5 ${badgeBg} text-xs`}>
              <p className="font-semibold mb-0.5 text-xs lg:text-sm text-gray-100">Buy DigitalBoutique Subscription.</p>
            </div>
            <div className="flex flex-col lg:flex-row gap-3">
              <button
                onClick={onClose}
                className="flex-1 text-center text-xs lg:text-sm font-medium py-1 px-2 w-fit rounded-xl border border-gray-700 text-gray-400 hover:text-gray-200 hover:border-gray-600 transition-all duration-200"
              >
                {reason === "expired" ? "Renew Subscription" : "Maybe Later"} 
              </button>
              { reason === "no_subscription" && (
                <button
                  onClick={() => {
                    setShowPDSubscription(true);
                  }}
                  className="flex-1 w-fit py-1 px-2 bg-pink-600 hover:bg-pink-500 rounded-lg text-white font-medium transition-colors"
                >
                  Subscribe Now
                </button>
              )}
            </div>
            <div className={`rounded-xl border px-2 py-1 mb-5 ${badgeBg} text-xs`}>
              <p className="font-semibold mb-0.5 text-xs lg:text-sm text-gray-100">Need help? Connect with us</p>
              <p className="text-gray-400 text-xs lg:text-sm">
                Reach out on{" "}
                <a href="https://wa.me/yourNumber" target="_blank" rel="noreferrer" className={`underline ${accentText}`}>
                  WhatsApp
                </a>{" "}
                or email{" "}
                <a href="mailto:support@yourdomain.com" className={`text-xs lg:text-sm underline ${accentText}`}>
                  contact@think5whys.com
                </a>
              </p>
            </div>
            {showPDSubscription && (
              <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] px-4 py-6 overflow-y-auto">
                <div className="bg-gray-900 border border-pink-500/50 rounded-3xl w-full max-w-2xl my-auto max-h-[90vh] overflow-y-auto">
                  <div className="flex justify-end px-4 pt-4">
                    <button
                      onClick={() => {
                        setShowPDSubscription(false);
                        onSubscribed && onSubscribed();
                      }}
                      className="text-xs lg:text-sm w-fit px-2 py-1 text-gray-400 hover:text-white leading-none"
                    >
                      ✕
                    </button>
                  </div>
                  <PDBuySubscription user={user}/>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const PDSkillBOK = () => {
  const authState   = useSelector(state => state.authReducer);
  const [fetchData] = useFetch();
  const token       = authState?.token;

  const userId = authState?.user?._id || "";
  const [subscriptions, setSubscriptions] = useState([]);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showInactiveAlert, setShowInactiveAlert] = useState(true);

  const [openModules, setOpenModules] = useState([]);
  const moduleRefs = useRef({});

  // const [message, setMessage] = useState("");
  // const [messageType, setMessageType] = useState(""); // "success" | "error"
  const [showCertificate, setShowCertificate] = useState(false);

  // Toggle function
  const toggleModule = (id) => {
    const isOpening = !openModules.includes(id);

    setOpenModules(prev =>
      prev.includes(id)
        ? prev.filter(m => m !== id)
        : [...prev, id]
    );

    // Scroll to the module smoothly when opening
    if (isOpening) {
      setTimeout(() => {
        const element = moduleRefs.current[id];
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100); // Small delay to let DOM render the opened section
    }
  };

  const emptyForm = () => ({
      userId: authState?.user?._id || "",
      userCode: authState?.user?.think5whyscode || "",
      name: authState?.user?.name || "",
      date: "", 
      age: "",

      m1_reaction: "", 
      m1_challenge: "", 
      m1_w1: "", 
      m1_w2: "", 
      m1_w3: "", 
      m1_w4: "", 
      m1_w5: "", 
      m1_score: "", 
      m1_insight: "",

      m2_love: "", 
      m2_good: "", 
      m2_world: "", 
      m2_value: "", 
      m2_ikigai: "",

      m3_distracted: "", 
      m3_switch: "", 
      m3_unfinished: "", 
      m3_procrastinate: "", 
      m3_focus: "", 
      m3_forget: "", 
      m3_shortform: "", 
      m3_multitask: "", 
      m3_leak: "", 
      m3_habit: "",

      m4_rel: "", 
      m4_occ: "", 
      m4_att: "", 
      m4_conf: "", 
      m4_health: "", 
      m4_ent: "", 
      m4_spirit: "", 
      m4_lowest: "", 
      m4_action: "",

      m5_smartphone: "", 
      m5_apps: "", 
      m5_communicate: "", 
      m5_independent: "", 
      m5_experiment: "", 
      m5_hour: "", 
      m5_fail: "", 
      m5_internet: "", 
      m5_strength: "", 
      m5_gap: "",

      m6_what: "", 
      m6_why: "", 
      m6_who: "", 
      m6_when: "", 
      m6_where: "", 
      m6_how: "", 
      m6_plan: "",

      m7_replace: "", 
      m7_mistakes: "", 
      m7_verify: "", 
      m7_learn: "", 
      m7_thinking: "", 
      m7_values: "", 
      m7_helps: [], 
      m7_human_lead: "",

      decl_commits: [], 
      decl_signature: "", 
      decl_date: "", 
      final_discovery: "", 
      final_action: "", 
      final_vision: "",
  });

  const [form, setForm]       = useState(emptyForm());
  const [editId, setEditId]   = useState(null);
  const [locked, setLocked]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [viewRec, setViewRec] = useState(null);
  const [active, setActive]   = useState("m1");
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const [msg, setMsg] = useState({ text: "", type: "" }); // type: "success" | "error" | "warning" | "info"
  const showMsg = (text, type = "info") => setMsg({ text, type });
  const clearMsg = () => setMsg({ text: "", type: "" });

  // ── toggle array values (for checkboxes / pill buttons) ──
  const toggleArr = (k, val) => {
    const arr = form[k] || [];
    setForm({ ...form, [k]: arr.includes(val) ? arr.filter(x => x !== val) : [...arr, val] });
  };

  // ── derived scores ──
  const attnTotal = ATTN_KEYS.reduce((s, k) => s + (parseInt(form[k]) || 0), 0);
  const readTotal = READINESS_QS.reduce((s, { k }) => s + (parseInt(form[k]) || 0), 0);
  const readLevel = readTotal <= 15 ? "Beginner" : readTotal <= 25 ? "Emerging" : readTotal <= 35 ? "Ready" : "Future Digital Entrepreneur";

  const applyRecord = (rec) => {
    setForm(rec);
    setEditId(rec._id);
    setLocked(!!rec.isLocked);
  };

  const resetForm = () => {
    setForm(emptyForm());
    setEditId(null);
    setLocked(false);
    setViewRec(null);
    setActive("m1");
    clearMsg();
  };

  const hasFetchedSubsRef = useRef(false);
  // API calls
  const fetchMySubs = async () => {
    try {
      const config = {
        url: "/pd/subscription/my/light",
        method: "post",
        data: { userId, product: "DigitalBoutique" },
        headers: { Authorization: authState.token },
      };
      const response = await fetchData(config, { silent: true });
      if (response?.status) {
        const subs = response.data || [];
        setSubscriptions(subs);
        setSelectedSubscription(subs[0] ?? null);
        hasFetchedSubsRef.current = true;
      }
    } catch (error) {
      console.error("Fetch Subscription Error:", error);
    } 
  };

  useEffect(() => {
    if (userId) fetchMySubs();
  }, [userId]);

  // --- Derived status ---
  const subscriptionStatus = getSubscriptionStatus(selectedSubscription);
  const isSubscriptionActive = subscriptionStatus.isActive;

  const [createLocked, setCreateLocked] = useState(false);
  const [isUpdated, setIsIpdated] = useState(false);

  // --- Gate loadRecords behind active subscription ---
  useEffect(() => {
    if (isSubscriptionActive) {
      loadRecords();
    }
  }, [isSubscriptionActive]);
  
  const loadRecords = async (silent = false) => {
    try {
      const res = await fetchData({
        url: "/pd/orientation/get",
        method: "post",
        data: { userId: authState?.user?._id },
        headers: { Authorization: token },
      });

      if (res?.success) {
        applyRecord(res.data);
        setCreateLocked(true);
        if (!silent) {
          if (res.data.isLocked) {
            showMsg("Your record is locked by the moderator. No further edits allowed.", "error");
          } else {
            showMsg("Your existing record has been loaded. You can update it below.", "info");
          }
        }
      }
      // if no record found — do nothing, form stays empty, Create button shows

    } catch (err) {
      const status = err?.response?.status;
      // 404 just means "no record yet" — that's normal for a new user, not an error
      if (status === 404) {
        setCreateLocked(false);
        return;
      }
      console.error("loadRecords failed:", err);
      if (!silent) showMsg("Failed to load your record. Please refresh.", "error");
    }
  };

  // ── Create (only when no record exists) ──
  const handleCreate = async () => {
    try {
      setLoading(true);
      clearMsg();

      const submitData = { ...form };
      if (authState?.user?._id)              submitData.userId   = authState.user._id;
      if (authState?.user?.think5whyscode)   submitData.userCode = authState.user.think5whyscode;

      const res = await fetchData({
        url: "/pd/orientation/create",
        method: "post",
        data: submitData,
        headers: { Authorization: token },
      });

      if (res?.success) {
        applyRecord(res.data);
        showMsg("Record created successfully. You can now update it anytime.", "success");
        // await loadRecords(true);
        return;
      }

      // Backend returned a non-success body (not a thrown error)
      if (res?.alreadyExists) {
        applyRecord(res.data);
        showMsg(
          res?.message || "A record already exists. It has been loaded for editing.",
          "warning"
        );
        return;
      }

      if (res?.isLocked) {
        setLocked(true);
        showMsg(
          res?.message ||
            "Your record is locked by the moderator and cannot be edited.",
          "error"
        );
        return;
      }

      showMsg(res?.message || "Unable to create record. Please try again.", "error");

    } catch (err) {
      const api = err?.response?.data;
      if (api?.alreadyExists) {
        applyRecord(api.data);
        showMsg(api?.message || "A record already exists and has been loaded.", "warning");
      } else if (api?.isLocked) {
        setLocked(true);
        showMsg(api?.message || "Record is locked and cannot be edited.", "error");
      } else {
        showMsg("Failed to create record. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Update ──
  const handleUpdate = async () => {
    if (!editId) {
      showMsg("No record found. Please create a record first.", "warning");
      return;
    }
    try {
      setLoading(true);
      clearMsg();

      const res = await fetchData({
        url: `/pd/orientation/${editId}`,
        method: "put",
        data: form,
        headers: { Authorization: token },
      });

      if (res?.success) {
        applyRecord(res.data);
        setIsUpdated(true);
        showMsg("Record updated successfully.", "success");
        // await loadRecords(true);
        return;
      }

      if (res?.isLocked) {
        setLocked(true);
        showMsg(
          res?.message || "Record has been locked by the moderator and cannot be edited.",
          "error"
        );
        return;
      }

      showMsg(res?.message || "Update failed. Please try again.", "error");

    } catch (err) {
      const api = err?.response?.data;
      if (api?.isLocked) {
        setLocked(true);
        showMsg(api?.message || "Record is locked and cannot be edited.", "error");
      } else {
        showMsg("Update failed. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  // ── Cancel / New ──
  const handleCancel = () => {
    resetForm();
  };

  // ── Edit from list (if you have a list view) ──
  const handleEdit = (rec) => {
    applyRecord(rec);
    setViewRec(null);
    setActive("m1");
    clearMsg();
  };

  const handleNew = () => {
    setForm(emptyForm());
    setEditId(null);
    setLocked(false);
    setViewRec(null);
    setActive("m1");
  };

  const dis = locked; // shorthand: inputs disabled when locked

  return (
    <div className="flex min-h-screen flex-col bg-gray-900 p-6 rounded-xl text-white border-t border-cyan-200 mt-2">
      {!isSubscriptionActive && showInactiveAlert && (
        <div className="min-h-screen w-full flex items-center justify-center">
          <SubscriptionInactiveAlert
            user={userId}
            reason={subscriptionStatus.reason}
            onClose={() => setShowInactiveAlert(false)}
            onSubscribed={fetchMySubs}
          />
        </div>
      )}

      {isSubscriptionActive && (
        <>
          {/* Top Header Info */}
          <div className="bg-[#0e1320] border border-gray-800 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[9px] text-xs text-gray-200 uppercase tracking-widest font-bold">Next Gen Entrepreneur</p>
                <p className="text-lg font-semibold text-white mt-1">Orientation Workshop</p>
              </div>

              {editId && (
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-bold ${locked ? "text-orange-400" : "text-indigo-400"}`}>
                    {locked ? "🔒 Locked" : "✏️ Editing"}
                  </span>
                  <Badge status={form.status || "draft"} />
                </div>
              )}
            </div>
            {form.status === "approved" && (
              <button
                onClick={() => setShowCertificate(true)}
                className="flex items-center gap-2 w-fit px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-sm font-semibold rounded-lg transition-all active:scale-95"
              >
                <span>📜</span>
                Download Certificate
              </button>
            )}
            {showCertificate && (
              <OrientationCertificate 
                form={form} 
                user={authState?.user} 
                onClose={() => setShowCertificate(false)} 
              />
            )}
            {/* Participant Info */}
            {active !== "table" && (
              <div className="mt-4 bg-[#0e1320] border border-gray-800 rounded-lg p-4">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <div>
                    <Label text="Participant Name" />
                    <input disabled className={iClass} placeholder="Full name" value={form.name}
                      onChange={e => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div>
                    <Label text="Date" />
                    <input type="date" className={iClass} value={form.date}
                      onChange={e => setForm({ ...form, date: e.target.value })} />
                  </div>
                  <div>
                    <Label text="Age" />
                    <input type="number" className={iClass} placeholder="Age" value={form.age}
                      onChange={e => setForm({ ...form, age: e.target.value })} disabled={dis} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Full Width Module Map + Accordion Content */}
          <div className="space-y-3">
            <div className="bg-[#0e1320] border border-gray-800 rounded-lg p-3">
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-3 px-2">MODULES</p>
              <div className="grid grid-cols-1 lg:grid-cols-4 gap-2">
                {MODULES.map(m => (
                  <button
                    key={m.id}
                    onClick={() => toggleModule(m.id)}
                    className={`flex items-center gap-3 px-2 py-1 text-left rounded-lg border transition-all
                      ${openModules.includes(m.id)
                        ? "border-indigo-500 bg-indigo-950/50 text-white"
                        : "border-gray-700 hover:border-gray-600 text-gray-200 hover:bg-gray-800/50"}`}
                  >
                    <span className="text-xl">{m.icon}</span>
                    <div>
                      <span className="block text-[10px] text-gray-400">{m.label}</span>
                      <span className="text-sm font-medium">{m.title}</span>
                    </div>
                  </button>
                ))}

                {/* Table View Button */}
                {/* <button
                  onClick={() => {
                    setActive("table");
                    setViewRec(null);
                  }}
                  className={`flex items-center gap-3 px-4 py-3 text-left rounded-lg border transition-all col-span-1 md:col-span-2 lg:col-span-1
                    ${active === "table" ? "border-indigo-500 bg-indigo-950/50 text-white" : "border-gray-700 hover:border-gray-600 text-gray-200 hover:bg-gray-800/50"}`}
                >
                  <span className="text-xl">📋</span>
                  <div>
                    <span className="block text-[10px] text-gray-400">VIEW ALL</span>
                    <span className="text-sm font-medium">All Records</span>
                  </div>
                </button> */}
              </div>
            </div>

            {/* Accordion Content Area */}
            <div className="space-y-4">
              {MODULES.map(m => (
                openModules.includes(m.id) && (
                  <div key={m.id} ref={(el) => (moduleRefs.current[m.id] = el)} 
                      className="bg-[#0e1320] border border-gray-800 rounded-lg overflow-hidden"
                  >
                    <div className="px-2 py-1 border-b border-gray-700 flex items-center justify-between bg-gray-700">
                      <div>
                        <p className="text-cyan-200 font-bold">{m.icon} {m.label} — {m.title}</p>
                      </div>
                      <button
                        onClick={() => toggleModule(m.id)}
                        className="w-fit px-2 py-1 text-gray-400 hover:text-white"
                      >
                        ▲
                      </button>
                    </div>

                    {/* Module Content */}
                    <div className="p-4 space-y-4">
                      {/* ── MODULE 1: Deep Thinking ── */}
                      {m.id === "m1" && (
                        <div className="space-y-3">
                          <p className="text-gray-200 text-xs">"97% of people operate at surface-level thinking."</p>

                          <Label text="When faced with a problem, you usually:" />
                          <select className={selClass} value={form.m1_reaction}
                            onChange={e => setForm({ ...form, m1_reaction: e.target.value })} disabled={dis}>
                            <option value="">Select one</option>
                            <option>React immediately</option>
                            <option>Ask a few questions</option>
                            <option>Analyze deeply before deciding</option>
                            <option>Seek multiple perspectives</option>
                          </select>

                          <p className="py-4 text-gray-100 text-xs font-bold">Think 5 Whys Exercise</p>
                          <Label text="Challenge you want to solve" />
                          <textarea className={taClass} style={{height: "150px"}} placeholder="Describe the challenge…"
                            value={form.m1_challenge} onChange={e => setForm({ ...form, m1_challenge: e.target.value })} disabled={dis} />

                          {[1, 2, 3, 4, 5].map(i => (
                            <div key={i}>
                              <Label text={`Why ${i}?`} />
                              <textarea className={taClass} style={{height: "150px", width: "100%"}} placeholder="Because…"
                                value={form[`m1_w${i}`]} onChange={e => setForm({ ...form, [`m1_w${i}`]: e.target.value })} disabled={dis} />
                            </div>
                          ))}

                          <Label text="Deep Thinking Self Score (1–10)" />
                          <ScoreSelect value={form.m1_score} onChange={v => setForm({ ...form, m1_score: v })} disabled={dis} />

                          <Label text="Key Insight" />
                          <textarea className={taClass} style={{height: "150px"}} placeholder="What did you discover?"
                            value={form.m1_insight} onChange={e => setForm({ ...form, m1_insight: e.target.value })} disabled={dis} />
                        </div>
                      )}

                      {/* ── MODULE 2: IKIGAI ── */}
                      {m.id === "m2" && (
                        <div className="space-y-3">
                          <p className="text-gray-200 text-xs">"Know why your existence is."</p>
                          {[
                            { k: "m2_love",   lbl: "What do you LOVE doing?",                hint: "Your passions…" },
                            { k: "m2_good",   lbl: "What are you GOOD at?",                  hint: "Your strengths…" },
                            { k: "m2_world",  lbl: "What does the WORLD need from you?",     hint: "Your potential impact…" },
                            { k: "m2_value",  lbl: "What can create VALUE and OPPORTUNITY?", hint: "Your unique intersection…" },
                            { k: "m2_ikigai", lbl: 'My Emerging IKIGAI — "I exist to…"',    hint: "Complete your IKIGAI statement…" },
                          ].map(({ k, lbl, hint }) => (
                            <div key={k}>
                              <Label text={lbl} />
                              <textarea className={taClass} style={{height: "150px"}} placeholder={hint}
                                value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} disabled={dis} />
                            </div>
                          ))}
                        </div>
                      )}

                      {/* ── MODULE 3: Attention Signals ── */}
                      {m.id === "m3" && (
                        <div className="space-y-3">
                          <p className="text-gray-200 text-xs">"Attention is the new currency." Rate from 1 (Never) to 5 (Always).</p>
                          <ScoreRow questions={ATTENTION_QS} form={form} setForm={setForm} maxScore={5} disabled={dis} />
                          <ScoreTotal value={attnTotal} max={40} />

                          <Label text="My Biggest Attention Leak" />
                          <textarea className={taClass} style={{height: "150px"}} placeholder="What distracts you most?"
                            value={form.m3_leak} onChange={e => setForm({ ...form, m3_leak: e.target.value })} disabled={dis} />

                          <Label text="One Habit I Will Improve" />
                          <textarea className={taClass} style={{height: "150px"}} placeholder="Specific habit change…"
                            value={form.m3_habit} onChange={e => setForm({ ...form, m3_habit: e.target.value })} disabled={dis} />
                        </div>
                      )}

                      {/* ── MODULE 4: ROACHES ── */}
                      {m.id === "m4" && (
                        <div className="space-y-3">
                          <p className="text-gray-200 text-xs">Daily Personal Operating Dashboard — rate each area 1–10.</p>
                          <table className="w-full text-xs mt-3">
                            <thead>
                              <tr className="text-gray-200 border-b border-gray-700">
                                <th className="text-left py-2 font-medium">Area</th>
                                <th className="text-center py-2 font-medium w-24">Score (1–10)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ROACHES.map(({ label, key }) => (
                                <tr key={key} className="border-b border-gray-800">
                                  <td className="py-2 text-gray-100">{label}</td>
                                  <td className="py-2 text-center">
                                    <ScoreSelect value={form[key]} onChange={v => setForm({ ...form, [key]: v })} disabled={dis} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>

                          <Label text="Lowest Score Area" />
                          <input className={iClass} placeholder="Which area scored lowest?"
                            value={form.m4_lowest} onChange={e => setForm({ ...form, m4_lowest: e.target.value })} disabled={dis} />

                          <Label text="One Action Tomorrow" />
                          <textarea className={taClass} style={{height: "150px"}} placeholder="What will you do tomorrow to improve?"
                            value={form.m4_action} onChange={e => setForm({ ...form, m4_action: e.target.value })} disabled={dis} />
                        </div>
                      )}

                      {/* ── MODULE 5: Digital Readiness ── */}
                      {m.id === "m5" && (
                        <div className="space-y-3">
                          <p className="text-gray-200 text-xs">Week 1 Readiness Check — rate from 1–5.</p>
                          <ScoreRow questions={READINESS_QS} form={form} setForm={setForm} maxScore={5} disabled={dis} />
                          <ScoreTotal value={readTotal} max={40} label={readLevel} />

                          <Label text="My Biggest Strength" />
                          <textarea className={taClass} style={{height: "150px"}} placeholder="What do you bring?"
                            value={form.m5_strength} onChange={e => setForm({ ...form, m5_strength: e.target.value })} disabled={dis} />

                          <Label text="My Biggest Gap" />
                          <textarea className={taClass} style={{height: "150px"}} placeholder="What needs work?"
                            value={form.m5_gap} onChange={e => setForm({ ...form, m5_gap: e.target.value })} disabled={dis} />
                        </div>
                      )}

                      {/* ── MODULE 6: 5W1H ── */}
                      {m.id === "m6" && (
                        <div className="space-y-3">
                          <p className="text-gray-200 text-xs">Before every important action, ask these questions.</p>
                          {[
                            { k: "m6_what",  lbl: "WHAT? — What exactly am I trying to achieve?" },
                            { k: "m6_why",   lbl: "WHY? — Why is this important?" },
                            { k: "m6_who",   lbl: "WHO? — Who benefits from this?" },
                            { k: "m6_when",  lbl: "WHEN? — When will I start?" },
                            { k: "m6_where", lbl: "WHERE? — Where will I execute it?" },
                            { k: "m6_how",   lbl: "HOW? — How will I make it happen?" },
                          ].map(({ k, lbl }) => (
                            <div key={k}>
                              <Label text={lbl} />
                              <textarea className={taClass} style={{height: "150px"}} placeholder="Your answer…"
                                value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} disabled={dis} />
                            </div>
                          ))}

                          <Label text="My Next 7-Day Action Plan" />
                          <textarea className={taClass} style={{height: "300px"}} placeholder="List your actions for the week…"
                            value={form.m6_plan} onChange={e => setForm({ ...form, m6_plan: e.target.value })} disabled={dis} />
                        </div>
                      )}

                      {/* ── MODULE 7: GenAI Boundaries ── */}
                      {m.id === "m7" && (
                        <div className="space-y-4">
                          <p className="text-gray-200 text-xs">"GenAI is a co-pilot, not a replacement for thinking."</p>

                          <p className="text-gray-100 text-xs font-bold">Mark True or False</p>
                          {GENAI_STATEMENTS.map(({ s, k }) => (
                            <div key={k} className="flex items-center justify-between py-2 border-b border-gray-800">
                              <span className="text-gray-100 text-xs">{s}</span>
                              <select className={selClass} value={form[k]}
                                onChange={e => setForm({ ...form, [k]: e.target.value })} disabled={dis}>
                                <option value="">—</option>
                                <option value="True">True</option>
                                <option value="False">False</option>
                              </select>
                            </div>
                          ))}

                          <p className="text-gray-100 text-xs font-bold">Where GenAI helps me</p>
                          <div className="flex flex-wrap gap-2">
                            {GENAI_HELPS.map(h => (
                              <button key={h} onClick={() => !dis && toggleArr("m7_helps", h)}
                                className={`w-fit text-xs px-2 py-1 rounded-full border transition-colors
                                  ${(form.m7_helps || []).includes(h)
                                    ? "bg-indigo-600 border-indigo-600 text-white"
                                    : "bg-gray-800 border-gray-700 text-gray-100 hover:border-gray-500"}
                                  ${dis ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}>
                                {h}
                              </button>
                            ))}
                          </div>

                          <Label text="Where Human Intelligence must lead" />
                          <textarea className={taClass} style={{height: "150px"}} placeholder="Areas AI should not lead…"
                            value={form.m7_human_lead} onChange={e => setForm({ ...form, m7_human_lead: e.target.value })} disabled={dis} />
                        </div>
                      )}

                      {/* ── DECLARATION ── */}
                      {m.id === "decl" && (
                        <div className="space-y-4">
                          <p className="text-gray-200 text-xs">I commit to:</p>

                          <div className="space-y-2">
                            {DECL_COMMITS.map(c => (
                              <label key={c} className={`flex items-center gap-2 text-xs text-gray-100 ${dis ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
                                <input type="checkbox" className="accent-indigo-500"
                                  checked={(form.decl_commits || []).includes(c)}
                                  onChange={() => !dis && toggleArr("decl_commits", c)} disabled={dis} />
                                {c}
                              </label>
                            ))}
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label text="Signature" />
                              <input className={iClass} placeholder="Type your name as signature"
                                value={form.decl_signature} onChange={e => setForm({ ...form, decl_signature: e.target.value })} disabled={dis} />
                            </div>
                            <div>
                              <Label text="Date" />
                              <input type="date" className={iClass}
                                value={form.decl_date} onChange={e => setForm({ ...form, decl_date: e.target.value })} disabled={dis} />
                            </div>
                          </div>

                          <div className="bg-gray-800/50 rounded-lg p-4 space-y-3">
                            <p className="text-gray-100 text-xs font-bold">Final Reflection</p>
                            <Label text="One thing I discovered about myself today" />
                            <textarea className={taClass} style={{height: "150px"}}
                              value={form.final_discovery} onChange={e => setForm({ ...form, final_discovery: e.target.value })} disabled={dis} />

                            <Label text="One action I will take within the next 24 hours" />
                            <textarea className={taClass} style={{height: "150px"}}
                              value={form.final_action} onChange={e => setForm({ ...form, final_action: e.target.value })} disabled={dis} />

                            <Label text="My Future Digital Entrepreneur Vision" />
                            <textarea className={taClass} style={{height: "150px"}}
                              value={form.final_vision} onChange={e => setForm({ ...form, final_vision: e.target.value })} disabled={dis} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )
              ))}
            </div>
          </div>

          {/* Bottom Action Buttons */}
          <div className="mt-4 flex gap-3">
            {isUpdated && (
              <p>{showMsg}</p>
            )} 
            {!editId && !locked && (
              <button onClick={handleCreate} disabled={loading || createLocked} 
                className="w-fit text-[10px] lg:text-xs bg-green-600 hover:bg-green-500 text-white font-bold px-1 py-1 rounded disabled:opacity-50">
                ➕ Create Record
              </button>
            )}
            {editId && !locked && (
              <button onClick={handleUpdate} disabled={loading} 
                className="w-fit text-[10px] lg:text-xs bg-indigo-600 hover:bg-indigo-500 text-white font-bold px-1 py-1 rounded disabled:opacity-50">
                ✅ Update
              </button>
            )}
            {editId && (
              <button onClick={handleCancel} 
                className="w-fit text-[10px] lg:text-xs bg-transparent text-gray-100 border border-gray-700 hover:border-gray-500 px-1 py-1 rounded disabled:opacity-50">
                ✖ Cancel
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default PDSkillBOK;
